import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdClose, MdCloudUpload, MdImage, MdCheckCircle } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { PexelsMediaSearch } from '../pexels';
import type { MediaType } from '../pexels/types';
import type { RootState } from '../../redux/store';
import { uploadImageRequest, resetImageUpload } from '../../redux/slices/imageUploadSlice';

interface MediaLibraryProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectMedia: (url: string, type: 'upload' | 'pexels') => void;
  selectedImages?: string[];
  onRemoveImage?: (index: number) => void;
}

interface PendingImage {
  id: string;
  file?: File;
  pexelsUrl?: string;
  preview: string;
  type: 'local' | 'pexels';
}

const MediaLibrary: React.FC<MediaLibraryProps> = ({ 
  isOpen, 
  onClose, 
  onSelectMedia,
  selectedImages = [],
  onRemoveImage
}) => {
  const dispatch = useDispatch();
  const { uploading, uploadedUrls, error, success } = useSelector((state: RootState) => state.imageUpload);
  
  const [activeTab, setActiveTab] = useState<'upload' | 'pexels'>('upload');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);

  // Handle successful upload
  useEffect(() => {
    if (success && uploadedUrls.length > 0) {
      // Get the last uploaded URL
      const lastUrl = uploadedUrls[uploadedUrls.length - 1];
      // Pass it to parent
      onSelectMedia(lastUrl, 'upload');
      
      // Remove the first pending image since it was processed
      setPendingImages(prev => prev.slice(1));
    }
  }, [success, uploadedUrls, onSelectMedia]);

  // Reset state when closing
  useEffect(() => {
    if (!isOpen) {
      setPendingImages([]);
      dispatch(resetImageUpload());
    }
  }, [isOpen, dispatch]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      // Add files to pending list
      Array.from(files).forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          setPendingImages(prev => [...prev, {
            id: `${Date.now()}-${Math.random()}`,
            file,
            preview: result,
            type: 'local'
          }]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    // Add dropped files to pending list
    Array.from(files).forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          setPendingImages(prev => [...prev, {
            id: `${Date.now()}-${Math.random()}`,
            file,
            preview: result,
            type: 'local'
          }]);
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const handlePexelsSelect = (url: string, type: MediaType) => {
    // Add Pexels URL to pending list
    setPendingImages(prev => [...prev, {
      id: `${Date.now()}-${Math.random()}`,
      pexelsUrl: url,
      preview: url,
      type: 'pexels'
    }]);
  };

  const removePendingImage = (id: string) => {
    setPendingImages(prev => prev.filter(img => img.id !== id));
  };

  const handleUploadNow = () => {
    if (pendingImages.length === 0 || uploading) return;

    // Upload the first pending image
    const firstImage = pendingImages[0];
    if (firstImage.file) {
      dispatch(uploadImageRequest({ file: firstImage.file }));
    } else if (firstImage.pexelsUrl) {
      dispatch(uploadImageRequest({ pexelsUrl: firstImage.pexelsUrl }));
    }
  };

  // Auto-upload next image when previous completes
  useEffect(() => {
    if (!uploading && pendingImages.length > 0 && success) {
      // Upload next image after a short delay
      const timer = setTimeout(() => {
        const nextImage = pendingImages[0];
        if (nextImage) {
          if (nextImage.file) {
            dispatch(uploadImageRequest({ file: nextImage.file }));
          } else if (nextImage.pexelsUrl) {
            dispatch(uploadImageRequest({ pexelsUrl: nextImage.pexelsUrl }));
          }
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [uploading, pendingImages, success, dispatch]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 z-40"
          />

          {/* Sliding Panel */}
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed left-0 top-0 bottom-0 w-[600px] bg-[#1a1a1a] z-50 shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
              <div>
                <h2 className="text-xl font-bold text-white">Media Library</h2>
                {selectedImages.length > 0 && (
                  <p className="text-sm text-gray-400 mt-1">
                    {selectedImages.length} image{selectedImages.length !== 1 ? 's' : ''} uploaded
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-700/50 rounded-full transition-colors"
              >
                <MdClose size={24} className="text-white" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-0 border-b border-gray-700">
              <button
                onClick={() => setActiveTab('upload')}
                className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-medium transition-colors relative ${
                  activeTab === 'upload'
                    ? 'text-[#EE4423] border-b-2 border-[#EE4423] bg-[#EE4423]/5'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                }`}
              >
                <MdCloudUpload size={20} />
                Upload Your Own
              </button>
              <button
                onClick={() => setActiveTab('pexels')}
                className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-medium transition-colors relative ${
                  activeTab === 'pexels'
                    ? 'text-[#EE4423] border-b-2 border-[#EE4423] bg-[#EE4423]/5'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                }`}
              >
                <MdImage size={20} />
                Pexels
              </button>
            </div>

            {/* Selected/Uploaded Images Preview */}
            {/* {selectedImages.length > 0 && (
              <div className="px-6 py-4 border-b border-gray-700">
                <div className="flex items-center gap-2 mb-3">
                  <MdCheckCircle className="text-green-500" size={18} />
                  <h3 className="text-white text-sm font-medium">Uploaded Images</h3>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  {selectedImages.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={image}
                        alt={`Uploaded ${index + 1}`}
                        className="w-full h-20 object-cover rounded-lg border-2 border-green-500"
                      />
                      {onRemoveImage && (
                        <button
                          onClick={() => onRemoveImage(index)}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                        >
                          <MdClose size={14} className="text-white" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )} */}

            {/* Pending Images */}
            {pendingImages.length > 0 && (
              <div className="px-6 py-4 border-b border-gray-700">
                <h3 className="text-white text-sm font-medium mb-3">
                  Ready to Upload ({pendingImages.length})
                </h3>
                <div className="grid grid-cols-4 gap-3">
                  {pendingImages.map((image, index) => (
                    <div key={image.id} className="relative group">
                      <img
                        src={image.preview}
                        alt={`Pending ${index + 1}`}
                        className={`w-full h-20 object-cover rounded-lg border-2 ${
                          index === 0 && uploading 
                            ? 'border-[#EE4423] animate-pulse' 
                            : 'border-gray-600'
                        }`}
                      />
                      {index === 0 && uploading && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                          <div className="w-6 h-6 border-2 border-[#EE4423] border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                      <button
                        onClick={() => removePendingImage(image.id)}
                        disabled={uploading}
                        className="absolute -top-2 -right-2 p-1 bg-gray-700 rounded-full hover:bg-gray-600 transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-50"
                      >
                        <MdClose size={14} className="text-white" />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={handleUploadNow}
                  disabled={uploading || pendingImages.length === 0}
                  className="mt-4 w-full px-4 py-3 bg-[#EE4423] hover:bg-[#ff5533] disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {uploading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <MdCloudUpload size={20} />
                      Upload Now ({pendingImages.length})
                    </>
                  )}
                </button>
                {error && (
                  <p className="mt-2 text-sm text-red-400">{error}</p>
                )}
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-hidden">
              {activeTab === 'upload' ? (
                <div className="h-full p-6">
                  {/* Upload Area */}
                  <div
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className="h-full border-2 border-dashed border-gray-700 rounded-2xl bg-[#212121] hover:border-[#EE4423] hover:bg-[#EE4423]/5 transition-all cursor-pointer flex flex-col items-center justify-center gap-4"
                  >
                    <div className="w-20 h-20 rounded-full bg-gray-800 flex items-center justify-center">
                      <MdCloudUpload size={40} className="text-gray-400" />
                    </div>
                    <div className="text-center">
                      <p className="text-white text-lg font-medium mb-2">
                        Drop your images here or click to browse
                      </p>
                      <p className="text-gray-400 text-sm">
                        Support for JPG, PNG, GIF · Multiple files supported
                      </p>
                    </div>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              ) : (
                <div className="h-full p-3">
                  <PexelsMediaSearch onSelectMedia={handlePexelsSelect} />
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MediaLibrary;
